import React, { ReactNode } from "react";
import { MacroResponse } from "types/misc";
import { Alert, Box, Typography } from "@mui/material";

export function BuildMacroResponseResult(
  response: MacroResponse | null | undefined,
  recommendValidation: boolean
): ReactNode {
  if (!response) return undefined;

  const failures = response.failures ?? [];
  const log = response.log ?? [];
  const summary = response.summaryMessage ?? "";

  const hasFailures = failures.length > 0;
  const hasLog = log.length > 0;
  const failuresPaddingLeft = failures.length >= 100 ? "3.5rem" : "2.5rem";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {/* Summary */}
      {summary && (
        <Box>
          <Typography variant="h5" sx={{ mb: 0.5 }}>
            Summary
          </Typography>
          <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
            {summary}
          </Typography>
        </Box>
      )}

      {/* Failures */}
      {hasFailures && (
        <Box>
          <Typography variant="h5" sx={{ mb: 0.5 }}>
            Failures ({failures.length})
          </Typography>

          <Box
            component="ol"
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              p: 1,
              maxHeight: 260,
              overflowY: "auto",

              // list behavior
              listStylePosition: "outside",
              paddingLeft: failuresPaddingLeft,
              margin: 0,

              // typography
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
              fontSize: 16,
              lineHeight: 1.1,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",

              // remove spacing after last item
              "& li:last-child": {
                marginBottom: 0,
              },
            }}
          >
            {failures.map((f, idx) => (
              <Box
                key={idx}
                component="li"
                sx={{
                  marginBottom: 2,
                }}
              >
                {f}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Full Log */}
      {hasLog && (
        <Box>
          <Typography variant="h5" sx={{ mb: 0.5 }}>
            Full log
          </Typography>

          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              p: 1,
              maxHeight: 260,
              overflowY: "auto",

              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
              fontSize: 16,
              lineHeight: 1.1,

              display: "flex",
              flexDirection: "column",
              gap: 0.75,
            }}
          >
            {log.map((line, idx) => (
              <Box
                key={idx}
                sx={{
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",

                  // hanging indent
                  paddingLeft: "1.5rem",
                  textIndent: "-1.5rem",
                }}
              >
                {line}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Optional: if everything is empty, still show something */}
      {!summary && !hasFailures && !hasLog && (
        <Typography variant="body1">
          No result details were returned.
        </Typography>
      )}

      {recommendValidation && (
        <Alert
          variant="outlined"
          severity="warning"
          sx={{
            mt: 1,
          }}
        >
          <strong>
            It is HIGHLY recommended that you validate your product line at this
            point!!!! This macro may not update product line validations.
          </strong>
        </Alert>
      )}
    </Box>
  );
}
